import React, { useMemo, useState, useRef, useEffect } from 'react';
import '../styles/Teams.css';

function getMondayOfWeek(weeksAgo = 0) {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff - weeksAgo * 7);
  return d.toISOString().slice(0, 10);
}

function getWeekKey(ta) {
  return ta.weekStartDate?.toDate
    ? ta.weekStartDate.toDate().toISOString().slice(0, 10)
    : (ta.weekStartDate?.slice?.(0, 10) ?? '');
}

const IconPencil = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconChevron = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const IconClose = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

function TeamsSection({
  sortedTeams, approvedEmployees, teamAverages,
  expandedTeams, editingTeamId, editingTeamName,
  setEditingTeamId, setEditingTeamName,
  toggleTeamExpanded, expandAllTeams, collapseAllTeams,
  setShowTeamModal, handleEditTeamName, handleMoveEmployee
}) {
  const [teamSortOrder, setTeamSortOrder] = useState('alphabetical');
  const [showRateInfo, setShowRateInfo] = useState(false);
  const infoRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setShowRateInfo(false);
    };
    if (showRateInfo) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRateInfo]);

  const twoWeekKeys = useMemo(() => [getMondayOfWeek(1), getMondayOfWeek(2)], []);

  const surveyMetrics = useMemo(() => {
    const teamMetrics = {};
    sortedTeams.forEach(team => {
      const memberCount = approvedEmployees.filter(emp => emp.teamId === team.id).length;
      const totalResponses = teamAverages
        .filter(ta => ta.teamId === team.id && twoWeekKeys.includes(getWeekKey(ta)))
        .reduce((a, b) => a + (b.surveyCount || 0), 0);
      teamMetrics[team.id] = {
        totalResponses,
        memberCount,
        avgResponsesPerUser: memberCount > 0 ? (totalResponses / memberCount).toFixed(1) : '0.0',
      };
    });
    return teamMetrics;
  }, [teamAverages, sortedTeams, approvedEmployees, twoWeekKeys]);

  const displayedTeams = useMemo(() => {
    const teamsWithMetrics = sortedTeams.map(team => ({
      ...team,
      metrics: surveyMetrics[team.id] || { totalResponses: 0, memberCount: 0, avgResponsesPerUser: '0.0' }
    }));
    switch (teamSortOrder) {
      case 'response-high':
        return [...teamsWithMetrics].sort((a, b) => parseFloat(b.metrics.avgResponsesPerUser) - parseFloat(a.metrics.avgResponsesPerUser));
      case 'response-low':
        return [...teamsWithMetrics].sort((a, b) => parseFloat(a.metrics.avgResponsesPerUser) - parseFloat(b.metrics.avgResponsesPerUser));
      default:
        return teamsWithMetrics;
    }
  }, [sortedTeams, surveyMetrics, teamSortOrder]);

  const getResponseBand = (avg) => {
    const val = parseFloat(avg);
    if (val >= 2.5) return { className: 'high',        label: 'High' };
    if (val >= 1.5) return { className: 'medium-high', label: 'Med-High' };
    if (val >= 1.0) return { className: 'medium',      label: 'Medium' };
    if (val >= 0.5) return { className: 'medium-low',  label: 'Med-Low' };
    return               { className: 'low',           label: 'Low' };
  };

  return (
    <>
      {showRateInfo && (
        <div className="rate-info-overlay" onClick={() => setShowRateInfo(false)}>
          <div className="rate-info-modal" ref={infoRef} onClick={(e) => e.stopPropagation()}>
            <div className="rate-info-header">
              <h3>Response Rate Bands</h3>
              <button className="rate-info-close" onClick={() => setShowRateInfo(false)}>
                <IconClose />
              </button>
            </div>
            <p className="rate-info-subtitle">
              Based on average survey submissions per team member over the last 2 completed weeks.
            </p>
            <div className="rate-info-bands">
              {[
                { cls: 'low',        label: 'Low',      desc: 'Less than 0.5 responses per person: team is rarely submitting surveys' },
                { cls: 'medium-low', label: 'Med-Low',  desc: '0.5 to 1.0 responses per person: roughly one survey per person per two weeks' },
                { cls: 'medium',     label: 'Medium',   desc: '1.0 to 1.5 responses per person: good engagement, around weekly' },
                { cls: 'medium-high',label: 'Med-High', desc: '1.5 to 2.5 responses per person: strong engagement, multiple times per week' },
                { cls: 'high',       label: 'High',     desc: '2.5 or more responses per person: excellent engagement' },
              ].map(({ cls, label, desc }) => (
                <div key={cls} className="rate-info-band">
                  <span className={`rate-band-dot ${cls}`}></span>
                  <div>
                    <strong>{label}</strong>
                    <span>{desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-header">
        <div><h1>Teams Management</h1></div>
        <div className="header-actions-row">
          <div className="sort-control-group">
            <label htmlFor="team-sort">Sort by:</label>
            <select id="team-sort" value={teamSortOrder} onChange={(e) => setTeamSortOrder(e.target.value)} className="sort-select">
              <option value="alphabetical">Alphabetical</option>
              <option value="response-high">Response Rate (High to Low)</option>
              <option value="response-low">Response Rate (Low to High)</option>
            </select>
          </div>
          <button className="btn-secondary" onClick={expandAllTeams}>Expand All</button>
          <button className="btn-secondary" onClick={collapseAllTeams}>Collapse All</button>
          <button className="btn-primary" onClick={() => setShowTeamModal(true)}>+ Create Team</button>
        </div>
      </div>

      <div className="team-survey-notice">
        <p>
          Survey data is only collected for teams with <strong>5 or more members</strong> at the time of submission.
          This protects employee anonymity.
        </p>
      </div>

      {displayedTeams.length === 0 ? (
        <div className="empty-state"><p>No teams yet. Create your first team!</p></div>
      ) : (
        <div className="teams-pivot-container">
          {displayedTeams.map(team => {
            const teamMembers = approvedEmployees
              .filter(emp => emp.teamId === team.id)
              .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
            const isExpanded = expandedTeams[team.id];
            const metrics = team.metrics;
            const band = getResponseBand(metrics.avgResponsesPerUser);

            return (
              <div key={team.id} className="team-pivot-card">
                <div className="team-pivot-header clickable" onClick={() => toggleTeamExpanded(team.id)}>
                  <div className="team-header-left">
                    <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                      <IconChevron />
                    </span>
                    {editingTeamId === team.id ? (
                      <input
                        type="text"
                        value={editingTeamName}
                        onChange={(e) => setEditingTeamName(e.target.value)}
                        onBlur={() => handleEditTeamName(team.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEditTeamName(team.id);
                          if (e.key === 'Escape') setEditingTeamId(null);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="team-name-input"
                        autoFocus
                      />
                    ) : (
                      <div>
                        <h3>{team.name}</h3>
                        <div className="team-stats-row">
                          <p className="team-member-count">
                            {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
                          </p>
                          <span className="team-stats-divider">•</span>
                          <div className="response-rate-badge">
                            <div className={`response-indicator ${band.className}`}>
                              <div className="indicator-dot"></div>
                              <span className="response-value">{band.label}</span>
                            </div>
                            <span className="response-label">Response Rate</span>
                            <button className="response-info-btn" onClick={(e) => { e.stopPropagation(); setShowRateInfo(true); }}>i</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="team-header-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn-edit-small"
                      onClick={() => { setEditingTeamId(team.id); setEditingTeamName(team.name); }}
                      title="Edit Team Name"
                    >
                      <IconPencil />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  teamMembers.length === 0 ? (
                    <div className="team-empty"><p>No members in this team yet</p></div>
                  ) : (
                    <div className="team-members-list">
                      {teamMembers.map(member => (
                        <div key={member.id} className="team-member-item">
                          <div className="member-info">
                            <span className="member-name">{member.name || member.email}</span>
                            <span className="member-email">{member.email}</span>
                          </div>
                          <select
                            className="move-select"
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) { handleMoveEmployee(member.id, e.target.value); e.target.value = ''; }
                            }}
                          >
                            <option value="">Move to...</option>
                            {displayedTeams.filter(t => t.id !== team.id).map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default TeamsSection;
